import { IonContent, IonSpinner, isPlatform } from '@ionic/react';
import { orderBy } from 'lodash';
import { ReactElement, useMemo, useRef } from 'react';
import { useMeasure } from 'react-use';
import {
  ChatMessage,
  useGetChatMessageQuery,
} from '../../api/graphql/generated';
import { getQueryParameter } from '../../util/router';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { InfiniteScroll } from '../scroll/infinite-scroll';
import './chat.css';
import { useListChatMessageQueryHelper } from './chat.hooks';
import { ConversationMessage } from './conversation-message';
import { ConversationReplyList } from './conversation-reply-list';
import { NewMessageChip } from './new-message-chip';
import { NewReply } from './new-reply';

const KEYBOARD_BAR_CLASS = 'chat-message-input-container-keyboard-bar';

export interface ConversationListProps {
  chatId: string;
  threadId: string;
}

const Conversation = (props: ConversationListProps): ReactElement => {
  const { chatId, threadId } = props;
  const cursor = getQueryParameter('cursor');
  const contentRef = useRef<HTMLIonContentElement>(null);
  const newReplyRef = useRef<HTMLDivElement>(null);

  const { data: getChatMessageData } = useGetChatMessageQuery({
    variables: { input: { id: threadId } },
    fetchPolicy: 'cache-first',
  });

  const {
    queryResult: { data: listChatData, loading, error, refetch },
    waitingOnFirstLoad,
    getMoreForInfiniteScroll,
    onScrollEnd,
    onNewMessageClick,
    hasNewMessage,
  } = useListChatMessageQueryHelper({
    chatId,
    threadId,
    contentRef,
    cursor,
    scroll: {
      auto: {
        enabled: true,
        direction: 'bottom',
      },
    },
    refetchOnViewEnter: true,
    clearCacheOnLoad: true,
  });

  const prevCursor = listChatData?.listChatMessages.prevCursor;

  const [chipRef, { height: chipHeight }] = useMeasure<HTMLDivElement>();
  const items = useMemo(
    () => listChatData?.listChatMessages.items ?? [],
    [listChatData]
  );

  const conversation = getChatMessageData?.getChatMessage;

  const replies: ChatMessage[] = useMemo((): ChatMessage[] => {
    return orderBy(items, ['publishedAt'], ['asc']);
  }, [items]);

  return (
    <IonContent
      forceOverscroll={isPlatform('ios') ? false : true}
      ref={contentRef}
      scrollEvents={true}
      onIonScrollEnd={onScrollEnd}
    >
      <LoadingRetry
        error={error}
        refetch={refetch}
        loading={loading || waitingOnFirstLoad}
        results={{
          hasResults: true,
          noResultsComponent: <></>,
        }}
      >
        {conversation && (
          <>
            <div className="chat-thread-list chat-conversation">
              <ConversationMessage conversation={conversation} />
              <InfiniteScroll
                className="content-center"
                onInfinite={() => {
                  getMoreForInfiniteScroll(prevCursor);
                }}
                disabled={!prevCursor || waitingOnFirstLoad}
                threshold={0.25}
                scrollElement={() => {
                  if (contentRef.current) {
                    return contentRef.current.getScrollElement();
                  }

                  return Promise.resolve(undefined);
                }}
                direction="top"
                loader={<IonSpinner className="ion-align-self-center" />}
                disableScrollOnPositionChange={isPlatform('ios')}
                debounceOnInfinite={isPlatform('capacitor') ? 250 : undefined}
              />
              <ConversationReplyList replies={replies} />
              <div ref={newReplyRef} className={'chat-reply-sticky-bottom'}>
                <NewReply
                  conversation={conversation}
                  onFocus={() => {
                    if (isPlatform('capacitor')) {
                      newReplyRef.current?.classList.add(KEYBOARD_BAR_CLASS);
                    }
                  }}
                  onBlur={() => {
                    if (isPlatform('capacitor')) {
                      newReplyRef.current?.classList.remove(KEYBOARD_BAR_CLASS);
                    }
                  }}
                />
              </div>
            </div>
          </>
        )}
        <div
          className="chat-chip-container-wrapper chat-chip-bottom"
          style={{
            transform: `translateY(-${chipHeight}px)`,
            visibility: hasNewMessage ? undefined : 'hidden',
          }}
        >
          <div ref={chipRef}>
            <NewMessageChip
              hidden={!hasNewMessage}
              onClick={() => onNewMessageClick()}
            />
          </div>
        </div>
      </LoadingRetry>
    </IonContent>
  );
};

export { Conversation };
